// @flow

import { put, call, all, takeEvery, select } from "redux-saga/effects";
import type { Action } from "redux-action";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import http from "@http";
import * as actions from "@modules/service/constants/actions";
import actionCreators from "@modules/service/actionCreator";
import type { NewShiftPattern, ShiftPattern } from "@modules/service/reducer/shift-patterns/types";
import { UnassignReassignShiftPatternTypeEnum } from "@modules/service/constants";
import {
  getSelectedShiftPattern,
  unassignReassignShiftPatternModalType,
} from "@modules/service/selectors";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type TotalResponse<T> = $Exact<{
  data: {
    content: T,
  },
}>;

type ShiftPatternsListRequest = $Exact<{
  clientId: string,
  pageable?: string,
}>;

type AssignedToShiftPatternRequest = $Exact<{
  clientId: string,
  shiftPattern: ShiftPattern,
  modalVisible: boolean,
}>;

export function* onGetShiftPatterns({
  payload,
}: Action<ShiftPatternsListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<ShiftPattern>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/shift-pattern`,
      {
        params: payload.pageable,
      },
    );

    yield put(actionCreators.GetShiftPatternsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetShiftPatternsError(e.response));
  }
}

type TotalShiftPatternsListRequest = $Exact<{
  clientId: string,
  limit: number,
}>;

export function* onGetTotalShiftPatterns({
  payload,
}: Action<TotalShiftPatternsListRequest>): Generator<*, void, TotalResponse<Array<ShiftPattern>>> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `api/v1/service-admin/client/${payload.clientId}/shift-pattern`,
      {
        params: {
          unpaged: true,
        },
      },
    );

    yield put(actionCreators.GetTotalShiftPatternsSuccess(response.data.content));
  } catch (e) {
    yield put(actionCreators.GetShiftPatternsError(e.response));
  }
}

export function* onGetEmployeePositionsAssignedToShiftPattern({
  payload,
}: Action<AssignedToShiftPatternRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.shiftPattern) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/shift-pattern/${payload.shiftPattern.id}/employee-position`,
    );

    yield all([
      put(actionCreators.GetEmployeePositionsAssignedToShiftPatternSuccess(response.data)),
      put(
        actionCreators.ToggleUnassignReassignShiftPatternModal({
          visible: payload.modalVisible,
          shiftPattern: payload.shiftPattern,
          type: UnassignReassignShiftPatternTypeEnum.EMPLOYEES,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.GetEmployeePositionsAssignedToShiftPatternError(e.response));
  }
}

export function* onGetDepartmentsAssignedToShiftPattern({
  payload,
}: Action<AssignedToShiftPatternRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.shiftPattern) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/shift-pattern/${payload.shiftPattern.id}/organisational-unit`,
    );

    yield all([
      put(actionCreators.GetDepartmentsAssignedToShiftPatternSuccess(response.data)),
      put(
        actionCreators.ToggleUnassignReassignShiftPatternModal({
          visible: true,
          shiftPattern: payload.shiftPattern,
          type: UnassignReassignShiftPatternTypeEnum.DEPARTMENTS,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.GetDepartmentsAssignedToShiftPatternError(e.response));
  }
}

type AddShiftPatternRequest = $Exact<{
  clientId: string,
  data: NewShiftPattern,
}>;

export function* onAddShiftPattern({
  payload,
}: Action<AddShiftPatternRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.data) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/shift-pattern`,
      payload.data,
    );

    yield put(sendSuccessMessage("SHIFT_CREATED"));
    yield put(actionCreators.AddShiftPatternSuccess());
    yield put(actionCreators.GetShiftPatterns({ clientId: payload.clientId }));
    yield put(actionCreators.ShowAddShiftPatternModal(false));
  } catch (e) {
    yield put(actionCreators.AddShiftPatternError(e.response));
  }
}

type UpdateShiftPatternRequest = $Exact<{
  clientId: string,
  data: ShiftPattern,
}>;

export function* onUpdateShiftPattern({
  payload,
}: Action<UpdateShiftPatternRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.data) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/shift-pattern/${payload.data.id}`,
      payload.data,
    );

    yield put(sendSuccessMessage("SHIFT_UPDATED"));
    yield put(actionCreators.UpdateShiftPatternSuccess());
    yield put(actionCreators.UpdateShiftPatternDraft(null));
    yield put(actionCreators.GetShiftPatterns({ clientId: payload.clientId }));
    yield put(actionCreators.ShowEditShiftPatternModal(false));
  } catch (e) {
    yield put(actionCreators.UpdateShiftPatternError(e.response));
  }
}

type DeleteShiftPatternRequest = $Exact<{
  clientId: string,
  id: number,
}>;

export function* onDeleteShiftPattern({
  payload,
}: Action<DeleteShiftPatternRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.id) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/shift-pattern/${payload.id}`,
    );

    yield put(sendSuccessMessage("SHIFT_UPDATED"));
    yield put(actionCreators.DeleteShiftPatternSuccess());
    yield put(actionCreators.GetShiftPatterns({ clientId: payload.clientId }));
    yield put(actionCreators.ShowDeleteShiftPatternModal(false));
  } catch (e) {
    yield put(actionCreators.DeleteShiftPatternError(e.response));
  }
}

type UnassignReassignItemsShiftPatternRequest = $Exact<{
  clientId: string,
  successMessage: string,
  data: $Exact<{
    ids: Array<number>,
    newShiftPatternId?: number,
  }>,
}>;

export function* onUnassignReassignItemsShiftPattern({
  payload,
}: Action<UnassignReassignItemsShiftPatternRequest>): Generator<*, void, void> {
  try {
    const shiftPattern = yield select(getSelectedShiftPattern);
    const actionType = yield select(unassignReassignShiftPatternModalType);

    if (
      !payload ||
      !payload.clientId ||
      !payload.data ||
      !shiftPattern ||
      !payload.successMessage
    ) {
      throw Error("Invalid payload provided");
    }

    const endpoint =
      actionType === UnassignReassignShiftPatternTypeEnum.DEPARTMENTS
        ? `/api/v1/service-admin/client/${payload.clientId}/shift-pattern/${shiftPattern.id}/organisational-unit/unassign`
        : `/api/v1/service-admin/client/${payload.clientId}/shift-pattern/${shiftPattern.id}/employee-position/unassign`;

    yield call(http.post, endpoint, payload.data);

    yield all([
      put(actionCreators.UnassignReassignItemsShiftPatternSuccess()),
      put(
        actionCreators.GetEmployeePositionsAssignedToShiftPattern({
          clientId: payload.clientId,
          shiftPattern,
          modalVisible: false,
        }),
      ),
      put(sendSuccessMessage(payload.successMessage)),
      put(actionCreators.GetShiftPatterns({ clientId: payload.clientId })),
      put(actionCreators.ToggleUnassignReassignItemsToShiftPatternModal(false)),
    ]);
  } catch (e) {
    yield put(actionCreators.UnassignReassignItemsShiftPatternError(e.response));
  }
}

// ROOT

export function* ShiftPatternsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(actions.GetShiftPatterns, onGetShiftPatterns),
    takeEvery(actions.GetTotalShiftPatterns, onGetTotalShiftPatterns),
    takeEvery(actions.AddShiftPattern, onAddShiftPattern),
    takeEvery(actions.UpdateShiftPattern, onUpdateShiftPattern),
    takeEvery(actions.DeleteShiftPattern, onDeleteShiftPattern),
    takeEvery(
      actions.GetEmployeePositionsAssignedToShiftPattern,
      onGetEmployeePositionsAssignedToShiftPattern,
    ),
    takeEvery(actions.GetDepartmentsAssignedToShiftPattern, onGetDepartmentsAssignedToShiftPattern),
    takeEvery(actions.UnassignReassignItemsShiftPattern, onUnassignReassignItemsShiftPattern),
  ]);
}

export default ShiftPatternsSagas;
